<template>
  <BuilderWrapper
    title="Navigation Buttons"
    helpful-tip="Customise the navigation buttons that are shown top right"
  >
    <LabelledInput
      label="Button location and order"
      helpful-tip="Change button order and move buttons into the dropdown menu"
    >
      <DraggableItems
        left-heading="Always Shown"
        right-heading="Dropdown Menu"
        :left-fields.sync="display"
        :right-fields.sync="dropdown"
      />
    </LabelledInput>

    <template v-for="({ enabled, label, iconConfigPath, nameConfigPath}) in allButtons">
      <NavButtonSettings
        v-if="enabled"
        :key="`${label}-button-settings`"
        :label="label"
        :icon-config-path="iconConfigPath"
        :name-config-path="nameConfigPath"
      />
    </template>
  </BuilderWrapper>
</template>

<script lang="ts">

import Component from 'vue-class-component'

import BuilderWrapper from '@/components/builder/UI/BuilderWrapper.vue'
import LabelledInput from '@/components/builder/UI/LabelledInput.vue'
import DraggableItems from '@/components/common/DraggableFields/DraggableItems.vue'
import NavButtonSettings from '@/components/builder/NavButtonsSettings/NavButtonSettings.vue'
import NavButtonsMixin from '@/mixins/NavButtonsMixin'

import { NavigationButtonDisplays } from '@/types'
import {
  SHOW_MAP_CONFIG_KEY,
  SEARCH_CONFIG_KEY,
  FAQ_CONFIG_KEY,
  DOWNLOAD_CONFIG_KEY,
  NAV_BTNS_CONFIG_KEY,
  DEFAULT_BUBBLES_NAV_NAME,
  DEFAULT_MAP_NAV_NAME,
  DEFAULT_SEARCH_NAME,
  DEFAULT_FAQ_NAME,
  DEFAULT_DROPDOWN_NAME,
  FILTER_BY_CATEGORY_KEY,
} from '@/constants'

const components = {
  BuilderWrapper,
  LabelledInput,
  DraggableItems,
  NavButtonSettings,
}

@Component({ components })
export default class NavButtonsSettings extends NavButtonsMixin {
  get navigationEnabled () {
    return Boolean(Object.entries(this.buttonConfig).find(([key, { display }]) => display === NavigationButtonDisplays.DROPDOWN && this.buttonEnabled(key)))
  }

  get allButtons () {
    return [
      {
        label: DEFAULT_BUBBLES_NAV_NAME,
        iconConfigPath: [NAV_BTNS_CONFIG_KEY, 'bubblesIcon'],
        nameConfigPath: [NAV_BTNS_CONFIG_KEY, 'bubblesName'],
        enabled: this.showMapEnabled,
        key: SHOW_MAP_CONFIG_KEY,
      },
      {
        label: DEFAULT_MAP_NAV_NAME,
        iconConfigPath: [NAV_BTNS_CONFIG_KEY, 'mapIcon'],
        nameConfigPath: [NAV_BTNS_CONFIG_KEY, 'mapName'],
        enabled: this.showMapEnabled,
        key: SHOW_MAP_CONFIG_KEY,
      },
      {
        label: DEFAULT_SEARCH_NAME,
        iconConfigPath: [SEARCH_CONFIG_KEY, 'icon'],
        nameConfigPath: [SEARCH_CONFIG_KEY, 'searchName'],
        enabled: this.searchSettingsEnabled,
        key: SEARCH_CONFIG_KEY,
      },
      {
        label: DEFAULT_FAQ_NAME,
        iconConfigPath: [FAQ_CONFIG_KEY, 'buttonIcon'],
        nameConfigPath: [FAQ_CONFIG_KEY, 'buttonName'],
        enabled: this.faqEnabled,
        key: FAQ_CONFIG_KEY,
      },
      {
        label: 'Download',
        iconConfigPath: [DOWNLOAD_CONFIG_KEY, 'buttonIcon'],
        nameConfigPath: [DOWNLOAD_CONFIG_KEY, 'buttonName'],
        enabled: this.downloadEnabled,
        key: DOWNLOAD_CONFIG_KEY,
      },
      {
        label: 'Filter',
        iconConfigPath: [FILTER_BY_CATEGORY_KEY, 'buttonIcon'],
        nameConfigPath: [FILTER_BY_CATEGORY_KEY, 'buttonName'],
        enabled: this.filterByCategoryEnabled,
        key: FILTER_BY_CATEGORY_KEY,
      },
      {
        label: DEFAULT_DROPDOWN_NAME,
        iconConfigPath: [NAV_BTNS_CONFIG_KEY, 'buttonIcon'],
        nameConfigPath: [NAV_BTNS_CONFIG_KEY, 'buttonName'],
        enabled: this.navigationEnabled,
        key: NAV_BTNS_CONFIG_KEY,
      },
    ]
  }

  get buttonSettings () {
    return Object.entries(this.buttonConfig).map(([key, config]) => {
      const button = this.allButtons.find(b => b.key === key)
      if (button) {
        const { label, enabled } = button
        return {
          ...config,
          key,
          label,
          enabled,
        }
      }
    })
  }

  get displayBtns () {
    return this.buttonSettings
      .filter(({ display }) => display === NavigationButtonDisplays.ON)
      .sort(({ order: first }, { order: second }) => {
        return first - second
      })
  }

  get dropdownBtns () {
    return this.buttonSettings
      .filter(({ display }) => display === NavigationButtonDisplays.DROPDOWN)
      .sort(({ order: first }, { order: second }) => {
        return first - second
      })
  }

  get display () {
    return this.displayBtns
      .filter(({ enabled }) => enabled)
      .map(({ key, label }) => ({ column: key, name: label }))
  }

  set display (newValue) {
    newValue
      .forEach(({ column: key }, index) => {
        this.updateConfig({
          configPath: [NAV_BTNS_CONFIG_KEY, 'buttonConfig', key],
          value: { order: index, display: NavigationButtonDisplays.ON },
        })
      })

    this.displayBtns
      .filter(({ key }) => newValue.every(({ column }) => key !== column))
      .forEach(({ key }, index) => {
        this.updateConfig({
          configPath: [NAV_BTNS_CONFIG_KEY, 'buttonConfig', key],
          value: { order: index + newValue.length, display: NavigationButtonDisplays.ON },
        })
      })
  }

  get dropdown () {
    return this.dropdownBtns
      .filter(({ enabled }) => enabled)
      .map(({ key, label }) => ({ column: key, name: label }))
  }

  set dropdown (newValue) {
    newValue
      .forEach(({ column: key }, index) => {
        this.updateConfig({
          configPath: [NAV_BTNS_CONFIG_KEY, 'buttonConfig', key],
          value: { order: index, display: NavigationButtonDisplays.DROPDOWN },
        })
      })

    this.dropdownBtns
      .filter(({ key }) => newValue.every(({ column }) => key !== column))
      .forEach(({ key }, index) => {
        this.updateConfig({
          configPath: [NAV_BTNS_CONFIG_KEY, 'buttonConfig', key],
          value: { order: index + newValue.length, display: NavigationButtonDisplays.DROPDOWN },
        })
      })
  }

  updateConfig (payload: { configPath: string[], value: any }) {
    this.$typedStore.primary.app.updateConfig(payload)
  }
}

</script>
